import "./Code.css";

export interface AICodeBlock {
  language?: string;
  title?: string;
  code?: string;
}

export function renderCodeBlocks(
  container: HTMLElement,
  blocks: AICodeBlock[],
): void {
  const usable = blocks
    .map((block) => ({
      ...block,
      code: normalizeCodeText(block.code || ""),
    }))
    .filter((block) => block.code.trim());

  if (!usable.length) {
    container.innerHTML = "";
    return;
  }

  container.innerHTML = usable
    .map((block, index) => {
      const language = normalizeLanguage(block.language || "text");
      const title = (block.title || "").trim();
      const code = block.code;
      const blockId = `ai-code-${Date.now()}-${index}`;

      return `
        <section class="ai-code-block" data-code-block="${blockId}">
          <div class="ai-code-header">
            <div class="ai-code-meta">
              ${
                title
                  ? `<strong>${escapeHtml(title)}</strong>`
                  : `<strong>${escapeHtml(languageLabel(language))}</strong>`
              }
              <span>${escapeHtml(languageLabel(language))}</span>
            </div>

            <button
              class="ai-code-copy"
              type="button"
              data-copy-code="${blockId}"
            >
              Copy
            </button>
          </div>

          <pre class="ai-code-pre"><code class="language-${escapeHtml(
            language,
          )}">${escapeHtml(code)}</code></pre>
        </section>
      `;
    })
    .join("");

  container
    .querySelectorAll<HTMLButtonElement>("[data-copy-code]")
    .forEach((button) => {
      button.addEventListener("click", async () => {
        const id = button.dataset.copyCode;
        if (!id) return;

        const section = container.querySelector<HTMLElement>(
          `[data-code-block="${cssEscape(id)}"]`,
        );
        const code = section?.querySelector("code")?.textContent || "";

        try {
          await navigator.clipboard.writeText(code);
          const previous = button.textContent;
          button.textContent = "Copied";

          window.setTimeout(() => {
            button.textContent = previous || "Copy";
          }, 1200);
        } catch {
          // Clipboard can be unavailable in restricted browser contexts.
        }
      });
    });
}

/**
 * Groq normally returns JSON "\n" sequences as real newlines after JSON.parse.
 * Some responses can arrive double-escaped ("\\n"), leaving literal \n text.
 *
 * Convert those transport escapes into actual formatting before rendering.
 * Only perform the conversion when the string has no real line breaks already,
 * which avoids changing legitimate escaped sequences inside normal multiline code.
 */
function normalizeCodeText(value: string): string {
  let code = value.replace(/\r\n/g, "\n");

  const hasRealNewline = code.includes("\n");
  const hasEscapedNewline = code.includes("\\n");

  if (!hasRealNewline && hasEscapedNewline) {
    code = code
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "  ");
  }

  return code.trimEnd();
}

function normalizeLanguage(language: string): string {
  const value = language.trim().toLowerCase();

  const aliases: Record<string, string> = {
    "c++": "cpp",
    cxx: "cpp",
    cc: "cpp",
    ts: "typescript",
    js: "javascript",
    py: "python",
    sh: "bash",
    shell: "bash",
  };

  return aliases[value] || value || "text";
}

function languageLabel(language: string): string {
  const labels: Record<string, string> = {
    cpp: "C++",
    c: "C",
    javascript: "JavaScript",
    typescript: "TypeScript",
    python: "Python",
    bash: "Bash",
    json: "JSON",
    html: "HTML",
    css: "CSS",
    text: "Code",
  };

  return labels[language] || language.toUpperCase();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }

  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
